import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyCurrencyField from '../../../components/fields/CompanyCurrencyField';
import DatePickerField from '../../../components/fields/DatePickerField';
import DisplayField from '../../../components/fields/DisplayField';
import InputField from '../../../components/fields/InputField';
import InputNumberField from '../../../components/fields/InputNumberField';
import SelectField from '../../../components/fields/SelectField';
import useCompanyFormats from '../../../utilities/useCompanyFormats';
import useFinderOptions from '../FinderVehicleListPage/useFinderOptions';
import { type FinderVehicleFormValues, colSpan, getDepreciation } from './shared';

type LtaSectionProps = {
    disabled?: boolean;
};

const LtaDetails = ({ disabled = false }: LtaSectionProps) => {
    const { t } = useTranslation('finderVehicle');

    const { coeCategory, ownerIdType } = useFinderOptions();

    const { values } = useFormikContext<FinderVehicleFormValues>();

    const { formatAmountWithCurrency } = useCompanyFormats(values.module.company.id);

    const depreciation = useMemo(() => {
        const today = dayjs();

        const { listing, lta } = values;

        const price = listing?.price?.value;
        const partRebateAmount = lta?.parfRebateAmount;
        const coeExpiryDate = lta?.coeExpiryDate;
        const registrationDate = lta?.firstRegistrationDate;

        if (isNil(price) || isNil(partRebateAmount)) {
            return undefined;
        }

        const computed = getDepreciation(price, partRebateAmount, coeExpiryDate, registrationDate, today);

        return formatAmountWithCurrency(computed);
    }, [formatAmountWithCurrency, values]);

    return (
        <Row gutter={14}>
            <Col {...colSpan}>
                <SelectField
                    {...t('finderVehicle:fields.lta.ownerIdType', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="lta.ownerIdType"
                    options={ownerIdType}
                    required
                    showSearch
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('finderVehicle:fields.lta.ownerId', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="lta.ownerId"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <>
                    <Col {...colSpan}>
                        <InputField
                            {...t('finderVehicle:fields.lta.make', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.make"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputField
                            {...t('finderVehicle:fields.lta.model', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.model"
                            required
                        />
                    </Col>
                </>
            )}
            <Col {...colSpan}>
                <InputField
                    {...t('finderVehicle:fields.lta.registrationNumber', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="lta.registrationNumber"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <Col {...colSpan}>
                    <InputNumberField
                        {...t('finderVehicle:fields.lta.transfers', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        name="lta.transfers"
                        required
                    />
                </Col>
            )}
            <Col {...colSpan}>
                <DatePickerField
                    {...t('finderVehicle:fields.lta.originalRegistrationDate', {
                        returnObjects: true,
                    })}
                    disabled={disabled}
                    name="lta.originalRegistrationDate"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <Col {...colSpan}>
                    <DatePickerField
                        {...t('finderVehicle:fields.lta.yearOfManufacture', { returnObjects: true })}
                        disabled={disabled}
                        name="lta.yearOfManufacture"
                        picker="year"
                    />
                </Col>
            )}
            <Col {...colSpan}>
                <CompanyCurrencyField
                    {...t('finderVehicle:fields.lta.omv', { returnObjects: true })}
                    company={values.module.company}
                    disabled={disabled}
                    name="lta.omv"
                    required
                />
            </Col>
            <Col {...colSpan}>
                <CompanyCurrencyField
                    {...t('finderVehicle:fields.lta.coe', { returnObjects: true })}
                    company={values.module.company}
                    disabled={disabled}
                    name="lta.coe"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <Col {...colSpan}>
                    <CompanyCurrencyField
                        {...t('finderVehicle:fields.lta.pqp', { returnObjects: true })}
                        company={values.module.company}
                        disabled={disabled}
                        name="lta.pqp"
                    />
                </Col>
            )}

            <Col {...colSpan}>
                <CompanyCurrencyField
                    {...t('finderVehicle:fields.lta.arf', { returnObjects: true })}
                    company={values.module.company}
                    disabled={disabled}
                    name="lta.arf"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <>
                    <Col {...colSpan}>
                        <InputField
                            {...t('finderVehicle:fields.lta.engineNumber', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.engineNumber"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputField
                            {...t('finderVehicle:fields.lta.chassisNumber', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.chassisNumber"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <DatePickerField
                            {...t('finderVehicle:fields.lta.firstRegistrationDate', { returnObjects: true })}
                            disabled={disabled}
                            name="lta.firstRegistrationDate"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputField
                            {...t('finderVehicle:fields.lta.colour', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.colour"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <DisplayField
                            label={t('finderVehicle:fields.lta.depreciation.label')}
                            value={depreciation}
                            disabled
                        />
                    </Col>
                    <Col {...colSpan}>
                        <DatePickerField
                            {...t('finderVehicle:fields.lta.parfEligibilityExpiryDate', { returnObjects: true })}
                            disabled={disabled}
                            name="lta.parfEligibilityExpiryDate"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <CompanyCurrencyField
                            {...t('finderVehicle:fields.lta.parfRebateAmount', { returnObjects: true })}
                            company={values.module.company}
                            disabled={disabled}
                            name="lta.parfRebateAmount"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SelectField
                            {...t('finderVehicle:fields.lta.coeCategory', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.coeCategory"
                            options={coeCategory}
                            required
                            showSearch
                        />
                    </Col>
                    <Col {...colSpan}>
                        <InputNumberField
                            {...t('finderVehicle:fields.lta.coePeriod', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name="lta.coePeriod"
                            required
                        />
                    </Col>
                </>
            )}
            <Col {...colSpan}>
                <DatePickerField
                    {...t('finderVehicle:fields.lta.coeExpiryDate', { returnObjects: true })}
                    disabled={disabled}
                    name="lta.coeExpiryDate"
                    required
                />
            </Col>
            {values.isLTAUploaded && (
                <Col {...colSpan}>
                    <InputNumberField
                        {...t('finderVehicle:fields.lta.power', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        name="lta.power"
                    />
                </Col>
            )}
        </Row>
    );
};

export default LtaDetails;
